import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { FaExclamationCircle } from 'react-icons/fa';
import { groupBy } from 'v3/utils/array';
import { dataTravel, validatePermission } from 'actions/index';
import api, { cooperplaceApi } from 'services/api';
import ApplicationLayout from 'v3/layouts/Application';
import Card, { DefaultLoadingBodyComponent } from 'v3/components/Card';
import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import { useModal, usePermission } from 'hooks';
import { formatDate } from 'utils/formatter';
import { useSnackbar } from 'v3/components/Snackbar';
import { BlockedTravelContext } from 'contexts/BlockedTravelProvider';
import moment from 'moment';
import { UserContext } from 'contexts/UserContext';
import { DELIVERY_TYPE_OPTIONS, UNDER_GRO_ANALYSIS_SLUG } from 'v3/utils/constants';
import { loadReplacementToFreightData, loadToFreightData, toReplacementPayload } from 'v3/components/FreightValues/utils';
import {
  InsuranceValueContextProvider,
  useInsuranceValue,
} from 'contexts/InsuranceValueContext';
import Header from './Header';
import MapTravel from './Map';
import Anchor from './Anchor';
import Documents from './Documents';
import Feed from './Feed';
import Attachments from './Attachments';
import DriverJourney from './DriverJourney';
import { ChangeRequest } from './ChangeRequest';
import { TravelProvider } from '..';
import * as Shared from '../shared';
import CteModal from './Modals/Cte';
import IssuingModal from './Modals/Issuing';
import { TravelShippingCompanyAttachments } from './TravelShippingCompany';
import PendingTravelCard from './PendingTravel';
import { TravelVehicleAttachments } from './TravelVehicle';
import { TravelDriverAttachments } from '../DriverAttachment';
import { TravelOccurrences } from './Occurrences';
import { FreightContracts } from './FreightContracts';
import TravelChecklist from './Checklist';
import CheckListDetailModal from './Checklist/CheckListDetailModal';
import * as Styled from './styles';
import RelatedTravels from './RelatedTravels';
import SecondChangeNoteModal from './Modals/SecondChangeNoteModal';

function TravelDetailsContent() {
  usePermission('VISUALIZAR_VIAGEM_DETALHES', { redirect: true });
  const { setEnabledToSelect, changedCompany } = useContext(UserContext);
  const {
    setLastMarginLiberation,
    setMargins,
    fetchBusinessMarginsValues,
    shippingServiceLevelMapped,
    isMarginAbove,
    updateItensToFetchMargin,
    margins,
  } = useContext(BlockedTravelContext);
  const { updateInsuranceValueByLoadId } = useInsuranceValue();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const [data, setData] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [isUnauthorized, setUnauthorized] = useState({
    unauthorized: false,
    message: '',
  });
  const [statuses, setStatuses] = useState([]);
  const [canUpdate, setCanUpdate] = useState(false);
  const [prevTravel, setPrevTravel] = useState([]);
  const [preSM, setPreSM] = useState(null);
  const [documents, setDocuments] = useState({
    ctes: [],
    mdfes: []
  });
  const [travelOccurrences, setTravelOccurrences] = useState([]);
  const [feed, setFeed] = useState([]);
  const [notFoundTravel, setNotFoundTravel] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [checklists, setChecklists] = useState([]);
  const [travelFreightContracts, setTravelFreightContracts] = useState([]);
  const [groAnalysisStatus, setGroAnalysisStatus] = useState(null);
  const [transhipmentChecklist, setTranshipmentChecklist] = useState(null);
  const [status, setStatus] = useState({});
  const [prevStatus, setPrevStatus] = useState({});
  const [steps, setSteps] = useState([]);
  const [tollsPlazaInfos, setTollsPlazaInfos] = useState();
  const [secondChangeNoteModal, setSecondChangeNoteModal] = useState(false);
  const mapCard = useRef(null);
  const travelDetailsCard = useRef(null);
  const loadDataCard = useRef(null);
  const travelDocumentsCard = useRef(null);
  const valuesCard = useRef(null);
  const bennerCard = useRef(null);
  const windowRef = useRef(null);
  const mercosulStatusCard = useRef(null);
  usePermission('VISUALIZAR_VIAGENS', { redirect: true });
  usePermission('VISUALIZAR_VIAGEM_DETALHES', { redirect: true });
  const snackbar = useSnackbar();

  const issuingModal = useModal();
  const cteModal = useModal();
  const transhipmentChecklistModal = useModal();
  const freightValuesRef = useRef();
  const freightValuesReplaceRef = useRef();

  const hasPermissionToEditGROAnalysis = validatePermission(
    'EDITAR_CAMPOS_EM_VALIDACAO_GR'
  );
  const isDisabledGROAnalysis = useMemo(() => {
    if (!data?.policy) {
      return false;
    }

    if (hasPermissionToEditGROAnalysis) {
      return false;
    }

    return groAnalysisStatus === UNDER_GRO_ANALYSIS_SLUG;
  }, [groAnalysisStatus]);

  async function fetchFeed() {
    try {
      const response = await api.get('travel-logs', {
        params: { attended_id: params.id },
      });

      const grouped = groupBy(response.data, item => formatDate(item.log_time));
      setFeed(grouped);
      dispatch(dataTravel(response.data));
    } catch (ex) {
      // Handle exception
    }
  }

  async function setFreightValues(loadData, travelData) {
    const props = loadToFreightData(loadData, travelData);
    await freightValuesRef.current?.init(props);
    if (loadData?.isReplacement && loadData?.replacements.length > 0) {
      const replacementProps = loadReplacementToFreightData(
        loadData,
        travelData
      );
      await freightValuesReplaceRef.current?.init({
        ...replacementProps,
      });
    }
  }

  async function fetchTravel() {
    try {
      setLoading(true);
      const response = await cooperplaceApi.get(`/travels/travel/${params.id}`);
      const travel = response.data;

      const toLocation = (location) => ({
        ...location,
        arrival_time: location.arrivalTime ? moment(location.arrivalTime).format('YYYY-MM-DD HH:mm') : null,
        effective_time: location.effectiveTime ? moment(location.effectiveTime).format('YYYY-MM-DD HH:mm') : null,
        scheduled_time: moment(location.scheduledTime).format('YYYY-MM-DD HH:mm'),
        start_schedule: moment(location.startSchedule).format('YYYY-MM-DD HH:mm'),
        identifier: location.id,
        city: location.cityModel,
        formatted_address: location.formattedAddress,
        country: location.cityModel?.province?.country,
        zip_code: location.zipCode,
      })

      const origin = travel?.loads?.loadOrigins?.map((location) => ({
        ...toLocation(location),
        type: DELIVERY_TYPE_OPTIONS.find(type => type.id === 'collect'),
      }));

      const destinations = travel?.loads?.loadDestinations?.map(location => ({
        ...toLocation(location),
        type: DELIVERY_TYPE_OPTIONS.find(type => type.id === location.action),
      }));

      const [marginValue, advanceValue] = shippingServiceLevelMapped(
        travel?.shipper?.shippingCompany?.descriptionServiceLevel
      );

      const itsOwnVehicle =
        travel?.vehicleAttended?.vehicle?.fleetType?.name === 'Próprio';

      setMargins({
        advance_money_percentage_external: itsOwnVehicle
          ? 0
          : travel?.loads?.advanceMoneyPercentage || advanceValue,
        benner_margin: itsOwnVehicle ? 0 : travel?.benner_margin || marginValue,
      });
      if (travel.travelMarginLiberation) {
        const [lastMarginLiberation] = travel?.travelMarginLiberation;
        setLastMarginLiberation(lastMarginLiberation || null);
      }

      await fetchBusinessMarginsValues(travel?.loads?.costCenter?.businessId);
      await updateItensToFetchMargin({
        loadId: travel?.loads?.id,
        mainVehicle: travel?.vehicle[0]?.vehicles,
        origin: travel?.loads?.loadOrigins[0],
        destinations: travel?.loads?.loadDestinations,
        shippingCompany: travel?.shipper?.shippingCompany,
        actualMargin: travel?.margin,
        clientId: travel?.loads?.taker?.id,
        costCenterId: travel?.loads?.costCenter?.externalId,
      });

      await setFreightValues(travel?.loads, travel);
      setData({
        ...travel,
        origin: origin[0],
        destinations: destinations,
      });
      setPrevTravel(travel);
      setGroAnalysisStatus(travel?.riskApproved);
    } catch (ex) {
        if (ex.response?.status === 401) {
        setUnauthorized({
          unauthorized: true,
          message: ex?.response?.data?.message || '',
        });
      }

      if (ex.response?.status === 404) {
        setNotFoundTravel(true);
      }
    } finally {
      setLoading(false);
      fetchFeed();
    }
  }

  async function fetchPreSM() {
    try {
      const response = await cooperplaceApi.get(`presm/${params.id}`);
      setPreSM(response.data);
    } catch (err) {
      setPreSM(null);
    }
  }


  async function fetchTravelDocuments() {
    try {
      const response = await cooperplaceApi.get(`travels/${params.id}/documents`);
      setDocuments(response.data);
    } catch (err) {
      setDocuments({
        ctes: [],
        mdfe: [],
      });
    }
  }

  async function fetchEmittedById(emittedId) {
    try {
      const response = await cooperplaceApi.get(`travels/${params.id}/emitteds/${emittedId}`);
      const ctes = documents.ctes.map(cte => {
        if (cte.id === emittedId) {
          return response.data;
        }
        return cte;
      });
      setDocuments({
        ...documents,
        ctes
      });
    } catch (err) {
      snackbar.show(<Text>Erro ao buscar documentos emitidos</Text>, {
        type: 'error',
      });
    }
  }

  async function retrieveOccurrences() {
    try {
      const response = await cooperplaceApi.get(
        `occurrences/logs/${params.id}`
      );
      setTravelOccurrences(response.data.message);
    } catch (error) {
      setTravelOccurrences([]);
    }
  }

  async function fetchTravelChecklists() {
    try {
      setIsFetching(true);
      const response = await cooperplaceApi.get(
        `travel-checklist/travel/${params.id}`
      );
      if (response?.data?.data) {
        setChecklists(response.data.data);
      }
      await retrieveOccurrences();
    } catch (error) {
      snackbar.show(<Text>Erro ao buscar checklists da viagem</Text>, {
        type: 'error',
      });
    } finally {
      setIsFetching(false);
    }
  }

  const getTravelFreightContracts = useCallback(async () => {
    try {
      const travelId = params?.id;
      if (travelId) {
        const response = await cooperplaceApi.get(
          `freight/byTravelId/${params?.id}`
        );
        setTravelFreightContracts(response.data);
      }
    } catch (error) {
      //
    }
  }, [params]);

  function handleCancel() {
    setData(prevTravel);
  }

  function openIssuingModal() {
    const canIssue = localStorage.getItem('issue_permission');

    if (canIssue === 'true') {
      issuingModal.open();
    } else {
      cteModal.open();
    }
  }

  async function fetchSteps() {
    const response = await api.get(`travel-status/steps/${params.id}`);
    setSteps(response.data);
  }

  async function changeStatus() {
    const IMPORT_PAYMENTS_VALID_TRAVEL_STATUS = [3, 5]; // em viagem e em análise

    try {
      const updateDataBenner = !(data?.loads?.costCenter?.travelWithoutExternalId && !data?.externalId);
      await cooperplaceApi.put(`travels/${data.id}/status`, {
        statusId: status.id,
      });

      if (updateDataBenner && IMPORT_PAYMENTS_VALID_TRAVEL_STATUS.includes(status.id)) {
        try {
          await cooperplaceApi.post(`payments/importFromBenner/${data.id}`);
        } catch { }
      }

      const EM_VIAGEM = 3;
      if (updateDataBenner && (status.id === EM_VIAGEM)) {
        try {
          await cooperplaceApi.post(`freight/importContracts/${data.id}`, {
            travelNumber: data?.load?.id_externo,
          });
        } catch { }
      }

      await fetchSteps();
      fetchPreSM();
      fetchFeed();
      snackbar.show(<Text>Você alterou o status da viagem com sucesso!</Text>, {
        type: 'success',
      });
      await fetchTravel();
    } catch (error) {
      
      let message = error.response?.data || 'Não foi possível alterar o status';
      if (
        typeof error?.response?.data === 'object' &&
        typeof error?.response?.data?.message === 'string'
      ) {
        message = error.response.data.message;
      }
      snackbar.show(<Text>{message}</Text>, {
        type: 'error',
      });
      setStatus(prevStatus);
    }
  }

  useEffect(() => {
    async function fetchTravelStatuses() {
      try {
        const response = await api.get('travel-status');
        setStatuses(response.data.filter(item => item.code !== 'ST4'));
      } catch (error) {
        // Handle exception
      }
    }

    fetchTravelStatuses();
  }, []);

  useEffect(() => {
    setUnauthorized({
      unauthorized: false,
      message: '',
    });
  }, [changedCompany]);

  useEffect(() => {
    fetchTravel();
    fetchPreSM();
  }, [params.id, changedCompany]);

  useEffect(() => {
    if (canUpdate) {
      fetchTravel();
      setCanUpdate(false);
    }
  }, [canUpdate]);

  useEffect(() => {
    if (params?.id) {
      getTravelFreightContracts();
      fetchTravelChecklists();
    }
  }, [params]);

  async function updateShipmentDiscounts(freightValues, loadId) {
    const discountsExtraFields = [...freightValues?.shipmentDiscounts?.extras];

    const issueRateAsExtraField = {
      label: 'Taxa de emissão',
      manual: true,
      value: freightValues?.shipmentDiscounts?.issueRate,
      showToDriver: false,
    };
    discountsExtraFields.push(issueRateAsExtraField);

    return cooperplaceApi.put('carg-shipment-discounts', {
      loadId,
      discountsExtraFields,
    });
  }

  async function updateShipmentTransfers(freightValues, loadId) {
    let transferExtraFields = [...freightValues?.shipmentTransfers?.extras];
    transferExtraFields = transferExtraFields.map(item => ({
      ...item,
      manual: true,
    }));
    return cooperplaceApi.put('carg-shipment-transfers', {
      loadId,
      transferExtraFields,
    });
  }

  async function updateCargInputs(freightValues, loadId) {
    let processedInputsExtraFields = [];
    if (freightValues?.discounts?.extras?.length > 0) {
      processedInputsExtraFields = [...freightValues?.discounts?.extras]?.map(
        item => {
          return {
            ...item,
            label: item.label.trim(),
            manual: true,
          };
        }
      );
    }

    processedInputsExtraFields.push({
      label: 'Seguro Coopercarga',
      manual: true,
      value: freightValues?.discounts?.coopercargaInsurance?.value,
      showToDriver: false,
    });

    await cooperplaceApi.put(`/carg-inputs`, {
      loadId,
      inputsExtraFields: processedInputsExtraFields,
    });
  }

  async function updateExtraFields(freightValues, loadId) {
    try {
      await Promise.all([
        updateCargInputs(freightValues, loadId),
        updateShipmentDiscounts(freightValues, loadId),
        updateShipmentTransfers(freightValues, loadId),
      ]);
    } catch (error) {
      //
    }
  }

  async function saveFreightValues(freightValues, travel, replacementPayload) {
    try {
      const editedData = {
        balance: freightValues?.balance?.value,
        negotiator_observation: freightValues?.shipmentTransfers?.negotiatorObservation,
        total_shipment_discounts: freightValues?.shipmentDiscounts?.total,
        total_shipment_transfer: freightValues?.shipmentTransfer?.total,
        advance_money_percentage: freightValues?.advanceMoney?.percentage,
        exchange_contract: freightValues?.erpExchangeValue,
        margin: freightValues?.travelMargin,
        benner_margin: margins?.benner_margin,
        replacements: replacementPayload ? [replacementPayload] : undefined,
      };

      await api.put(`travel-data/${travel.id}/freight-values`, editedData);
      await updateExtraFields(freightValues, travel.loads.id);
    } catch (err) {
      
    }
  }

  async function updateFreightValues() {
    const insuranceValue = await updateInsuranceValueByLoadId(data?.loads?.id);

    if (insuranceValue !== null && insuranceValue !== undefined) {
      const insuranceCoopercargaValue =
        freightValuesRef?.current?.setInsuranceValueBenner(insuranceValue);
      const freightValues = await freightValuesRef?.current?.getValues();
      const freightValuesData = {
        ...freightValues,
        discounts: {
          ...freightValues.discounts,
          coopercargaInsurance: insuranceCoopercargaValue,
        },
      }

      let replacementPayload = null
      if (data?.loads?.isReplacement) {
        const insuranceReplacementValue =
          freightValuesReplaceRef?.current?.setInsuranceValueBenner(insuranceValue);

        const replacementFreightValues = await freightValuesReplaceRef?.current?.getValues();

        replacementPayload = toReplacementPayload({
          ...replacementFreightValues,
          discounts: {
            ...replacementFreightValues.discounts,
            coopercargaInsurance: insuranceReplacementValue,
          },
        })
      }

      await saveFreightValues(freightValuesData, data, replacementPayload);
    }
  }

  const showReplacementButton = useMemo(() => {
    if (!data?.loads?.replacements?.length || !data?.loads?.isReplacement) {
      return false;
    }
    const hasRelatedTravelActive = data?.travelRelated?.filter(travel => !['ST10', 'ST7'].includes(travel.relatedTravel?.travelStatus?.code))
    if (hasRelatedTravelActive.length > 0) {
      return false;
    }

    if (!['ST5', 'ST6'].includes(data?.travelStatus?.code)) {
      return false;
    }

    return true;
  }, [
    data?.travelStatus?.code,
    data?.loads?.replacements?.length,
    data?.loads?.isReplacement,
    data?.travelRelated
  ]);

  function handleIssueCte() {
    issuingModal.close();

    window.open(
      `${import.meta.env.VITE_ISSUING_FRONTEND_URL}auth/${localStorage.getItem('token')}?redirect_to=/emitir/${data.id}`
    );
  }

  const saveTollsPlaza = async (tollsPlaza, loadId, travelId) => {
    try {
      const payload = {
        travelId: travelId,
        loadId: loadId,
        requestError: tollsPlaza?.requestError,
        tollsPlaza: tollsPlaza?.tollsPlaza,
        totalsTollsPlaza: {
          totalTollValue: tollsPlaza?.totalTollValue,
          totalTollTagValue: tollsPlaza?.totalTollTagValue,
          totalKm: tollsPlaza?.totalKm,
          expectedTime: tollsPlaza?.expectedTime,
          identifierHistory: tollsPlaza?.identifierHistory,
          codPolilyne: tollsPlaza?.codPolilyne,
          totalAxes: tollsPlaza?.totalAxes,
        }
      };

      const response = await cooperplaceApi.post('tollsPlaza/insert-tolls-plaza-load', payload);

      return response.data;

    } catch (error) {
      // ignore
    }
  };

  const fetchTollsPlaza = async (vehicleTypeId, implementsTypesIds, polyline, options = {}) => {

    try {
      const response = await cooperplaceApi.post(`tollsPlaza/integration-extratta`, {
        polyline: polyline,
        vehiclesTypesIds: vehicleTypeId,
        implementsTypesIds: implementsTypesIds,
      });

      setTollsPlazaInfos(response.data)
      return response.data;

    } catch (error) {
      if (!options?.silentError) {
        snackbar.show(
          <Text>{error?.response?.data?.message || "Erro ao recalcular praças de pedágio"}</Text>,
          { type: "error" }
        );
      }

      return null;
    }
  };

  if (notFoundTravel) {
    return (
      <ApplicationLayout>
        <Card>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            className="text-center"
          >
            <Text type="header" color="error">
              <FaExclamationCircle
                size={26}
                color="#F64E60"
                className="mr-2 mb-1"
              />
              Essa viagem não existe
            </Text>
          </div>
        </Card>
      </ApplicationLayout>
    );
  }

  if (isUnauthorized.unauthorized) {
    setEnabledToSelect(true);
    return (
      <ApplicationLayout>
        <Card>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            className="text-center"
          >
            <Text type="header" color="error">
              <FaExclamationCircle
                size={26}
                color="#F64E60"
                className="mr-2 mb-1"
              />
              Você não tem permissão para acessar essa viagem!
            </Text>
            <Text type="header" color="error">
              {isUnauthorized.message}
            </Text>
          </div>
        </Card>
      </ApplicationLayout>
    );
  }
  return (
    <TravelProvider
      value={{
        data,
        preSM,
        fetchPreSM,
        fetchTravelDocuments,
        fetchEmittedById,
        documents,
        setData,
        isLoading,
        fetchTravel,
        statuses,
        openIssuingModal,
        cteModal,
        handleIssueCte,
        handleCancel,
        travelOccurrences,
        retrieveOccurrences,
        travelFreightContracts,
        setTravelFreightContracts,
        getTravelFreightContracts,
        feed,
        fetchFeed,
        fetchTravelChecklists,
        setIsFetching,
        isFetching,
        checklists,
        setTranshipmentChecklist,
        transhipmentChecklistModal,
        setSecondChangeNoteModal,
        status: {
          changeStatus,
          setStatus,
          prevStatus,
          setPrevStatus,
          status,
          fetchSteps,
          steps,
        },
        isMarginAbove,
        groAnalysisStatus,
        isDisabledGROAnalysis,
        saveTollsPlaza,
        tollsPlazaInfos,
        setTollsPlazaInfos,
        fetchTollsPlaza
      }}
    >
      <SecondChangeNoteModal
        isOpen={secondChangeNoteModal}
        setIsOpen={setSecondChangeNoteModal}
      />
      <IssuingModal
        open={issuingModal.isOpen}
        handleHide={() => {
          issuingModal.close();
        }}
        handleFillCte={() => {
          issuingModal.close();
          cteModal.open();
        }}
        handleIssueCte={handleIssueCte}
      />
      <CteModal
        showModal={cteModal.isOpen}
        handleClose={() => cteModal.close()}
        status={data?.travelStatus}
      />
      {transhipmentChecklist?.id && (
        <CheckListDetailModal
          mode="travel"
          checklist={transhipmentChecklist}
          modalHook={transhipmentChecklistModal}
          travelData={data}
          fetchTravelChecklists={fetchTravelChecklists}
          onConfirmResponse={changeStatus}
          handleClose={() => setStatus(prevStatus)}
          fetchFeed={fetchFeed}
          fetchTravel={fetchTravel}
        />
      )}
      <ApplicationLayout
        title="Detalhes da viagem"
        RightComponent={
          <div className="d-inline-flex align-items-center" style={{ gap: '12px' }}>
            {showReplacementButton && (
              <Button
                variant="success"
                onClick={() => setSecondChangeNoteModal(true)}
                className="py-2"
              >
                <Text weight={500} type="regular">
                  Criar 2ª Troca Nota
                </Text>
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
              className="py-2"
            >
              <Text weight={500} type="regular">
                Voltar
              </Text>
            </Button>
            {(!['ST2', 'BLOCKED', 'ST7'].includes(data?.travelStatus?.code) ||
              data?.loads?.costCenter?.showLoadingOrder) && <Shared.PDF />}
          </div>
        }
      >
        <Anchor
          window={windowRef}
          map={mapCard}
          travelDetails={travelDetailsCard}
          loadData={loadDataCard}
          travelDocuments={travelDocumentsCard}
          values={valuesCard}
          benner={bennerCard}
          mercosul={mercosulStatusCard}
        />

        <Card
          ref={mapCard}
          loading={isLoading}
          LoadingBodyComponent={() => (
            <DefaultLoadingBodyComponent linesCount={7} perLine={2} />
          )}
          header={
            <>
              <div className="d-flex align-items-center">
                <Text type="header" color="dark">
                  Viagem
                  {data.travelNumber && ` - ${data.travelNumber}`}
                </Text>
                {data?.loads?.replacementOrder === 1 && (
                  <Styled.Badge className="mx-2" size="sm" pill type="light">
                    <Text type="label">1ª Troca de Nota</Text>
                  </Styled.Badge>
                )}
                {data?.loads?.replacementOrder === 2 && (
                  <Styled.Badge className="mx-2" size="sm" pill type="purple">
                    <Text type="label">2ª Troca de Nota</Text>
                  </Styled.Badge>
                )}
              </div>
              <Text type="label" color="gray">
                Atendido por {data?.user?.username} -{' '}
                {moment.utc(data?.createdAt).format('DD/MM/YYYY')}
              </Text>
            </>
          }
        >
          <Header />
          <MapTravel />
        </Card>
        <Shared.Locations
          onUpdateAddress={updateFreightValues}
          travelDetailsRef={travelDetailsCard}
        />
        <Row>
          <Col md={6} xs={12}>
            <Shared.Load loadDataRef={loadDataCard} />
            <Shared.MercosulStatus mercosulRef={mercosulStatusCard} />
            <Shared.BennerStatus bennerRef={bennerCard} />
            <Shared.Negotiator />
            <TravelOccurrences />
            <Shared.Approved />
            <Shared.ApprovedNegotiator />
          </Col>

          <Col md={6} xs={12}>
            <Shared.VehicleAndDriver
              onChangeTransportInformation={updateFreightValues}
              freightValuesRef={freightValuesRef}
              freightValuesReplaceRef={freightValuesReplaceRef}
            />
            <Shared.Data />
            <Shared.SalesApproval />
            <TravelChecklist />
            <Shared.PreSMStatus />
            <PendingTravelCard /> 
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Shared.LoadFreight
              valuesRef={valuesCard}
              updateFreightValues={saveFreightValues}
              freightValuesRef={freightValuesRef}
              freightValuesReplaceRef={freightValuesReplaceRef}
            />
          </Col>
          <Col xs={12}>
            <Shared.PolicyRiskManagerCard />
          </Col>
          <Col xs={12}>
            <ChangeRequest />
          </Col>
          <Col xs={12}>
            <FreightContracts />
          </Col>
          <Col xs={12}>
            <TravelVehicleAttachments />
            <TravelShippingCompanyAttachments />
            <TravelDriverAttachments />
          </Col>
          <Col xs={12}>
            <Attachments />
            <Documents
              travelDocumentsRef={travelDocumentsCard}
              setCanUpdate={setCanUpdate}
            />
          </Col>
          {data?.travelRelated?.length > 0 && (
            <Col xs={12}>
              <RelatedTravels />
            </Col>
          )}
          <Col md={6} xs={12}>
            <Feed />
          </Col>

          <Col md={6} xs={12}>
            <DriverJourney />
          </Col>
        </Row>
      </ApplicationLayout>
    </TravelProvider>
  );
}

export default function TravelDetails() {
  return (
    <InsuranceValueContextProvider>
      <TravelDetailsContent />
    </InsuranceValueContextProvider>
  );
}
